// stores/action-flow-store.ts
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ActionFlowConfigurationDTO, ActionFlowSubscriptionDTO } from '@/shared/api/model';

interface ActionFlowState {
    // Configuration state
    configurations: ActionFlowConfigurationDTO[];
    activeConfiguration: ActionFlowConfigurationDTO | null;
    configurationFilter: Record<string, any>;

    // Subscription state
    subscriptions: ActionFlowSubscriptionDTO[];
    activeSubscription: ActionFlowSubscriptionDTO | null;
    formConfigs: {
        runtime: Record<string, string>;
        edit: Record<string, string>;
    };

    // UI state
    isLoading: boolean;
    error: Error | null;
}

interface ActionFlowActions {
    // Configuration actions
    setConfigurations: (configurations: ActionFlowConfigurationDTO[]) => void;
    setActiveConfiguration: (configuration: ActionFlowConfigurationDTO | null) => void;
    setConfigurationFilter: (filter: Record<string, any>) => void;
    addConfiguration: (configuration: ActionFlowConfigurationDTO) => void;
    updateConfiguration: (configuration: ActionFlowConfigurationDTO) => void;
    removeConfiguration: (id: string) => void;

    // Subscription actions
    setSubscriptions: (subscriptions: ActionFlowSubscriptionDTO[]) => void;
    setActiveSubscription: (subscription: ActionFlowSubscriptionDTO | null) => void;

    // Form config actions
    setFormConfig: (mode: 'runtime' | 'edit', domain: string, subscriptionId: string, config: string) => void;
    getFormConfig: (mode: 'runtime' | 'edit', domain: string, subscriptionId: string) => string | null;

    // UI actions
    setLoading: (isLoading: boolean) => void;
    setError: (error: Error | null) => void;
    reset: () => void;
}

type ActionFlowStore = ActionFlowState & ActionFlowActions;

const initialState: ActionFlowState = {
    configurations: [],
    activeConfiguration: null,
    configurationFilter: {},
    subscriptions: [],
    activeSubscription: null,
    formConfigs: {
        runtime: {},
        edit: {}
    },
    isLoading: false,
    error: null,
};

export const useActionFlowStore = create<ActionFlowStore>()(
    devtools(
        (set, get) => ({
            ...initialState,

            // Configuration actions
            setConfigurations: (configurations) =>
                set({ configurations }),

            setActiveConfiguration: (configuration) =>
                set({ activeConfiguration: configuration }),

            setConfigurationFilter: (filter) =>
                set({ configurationFilter: filter }),

            addConfiguration: (configuration) =>
                set((state) => ({
                    configurations: [...state.configurations, configuration],
                })),

            updateConfiguration: (configuration) =>
                set((state) => ({
                    configurations: state.configurations.map((config) =>
                        config.id === configuration.id ? configuration : config
                    ),
                })),

            removeConfiguration: (id) =>
                set((state) => ({
                    configurations: state.configurations.filter((config) => config.id !== id),
                })),

            // Subscription actions
            setSubscriptions: (subscriptions) =>
                set({ subscriptions }),

            setActiveSubscription: (subscription) =>
                set({ activeSubscription: subscription }),

            // Form config actions
            setFormConfig: (mode, domain, subscriptionId, config) =>
                set((state) => ({
                    formConfigs: {
                        ...state.formConfigs,
                        [mode]: {
                            ...state.formConfigs[mode],
                            [`${domain}:${subscriptionId}`]: config,
                        },
                    },
                })),

            getFormConfig: (mode, domain, subscriptionId) => {
                const { formConfigs } = get();
                return formConfigs[mode][`${domain}:${subscriptionId}`] || null;
            },

            // UI actions
            setLoading: (isLoading) =>
                set({ isLoading }),

            setError: (error) =>
                set({ error }),

            reset: () =>
                set(initialState),
        }),
        { name: 'ActionFlowStore' }
    )
);

// Type-safe selector hooks
export const useFormConfig = (mode: 'runtime' | 'edit', domain: string, subscriptionId: string) => {
    return useActionFlowStore((state) => state.formConfigs[mode][`${domain}:${subscriptionId}`]);
};

export const useConfiguration = (id: string) => {
    return useActionFlowStore(
        (state) => state.configurations.find((config) => config.id === id)
    );
};